import {
  asc,
  desc,
  eq,
  isNotNull,
  like,
  or,
  type PowerSyncDrizzleDB,
  recipe,
  recipe_ingredient,
  recipe_version,
} from "@mealplan/powersync";
import { and } from "drizzle-orm";
import { liveRecipeFilters } from "../live/filters";

/** Parsea un campo JSON-array (steps/tags) tolerante a datos sucios. */
export function parseStringArray(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is string => typeof item === "string");
    }
    return [];
  } catch {
    return [];
  }
}

export function serializeStringArray(values: string[]): string {
  return JSON.stringify(values.map((v) => v.trim()).filter(Boolean));
}

/**
 * Recetas del usuario, filtradas opcionalmente por texto (nombre) y etiqueta.
 * El filtro de etiqueta usa LIKE sobre el JSON (`"tag"`): exacto suficiente
 * para F1 dado que las etiquetas se serializan con comillas.
 */
export function getRecipesQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  options: { search?: string; tag?: string } = {},
) {
  // Vista viva: excluye recetas en la papelera (vía capa live centralizada).
  const conditions = [eq(recipe.user_id, userId), ...liveRecipeFilters()];
  const search = options.search?.trim();
  if (search) {
    const pattern = `%${search}%`;
    const nameOrTag = or(like(recipe.name, pattern), like(recipe.tags, pattern));
    if (nameOrTag) {
      conditions.push(nameOrTag);
    }
  }
  if (options.tag) {
    conditions.push(like(recipe.tags, `%"${options.tag}"%`));
  }
  return db
    .select()
    .from(recipe)
    .where(and(...conditions))
    .orderBy(desc(recipe.updated_at));
}

export function getRecipeByIdQuery(db: PowerSyncDrizzleDB, recipeId: string) {
  return db.select().from(recipe).where(eq(recipe.id, recipeId)).limit(1);
}

export function getRecipeIngredientsQuery(db: PowerSyncDrizzleDB, recipeId: string) {
  return db
    .select()
    .from(recipe_ingredient)
    .where(eq(recipe_ingredient.recipe_id, recipeId))
    .orderBy(asc(recipe_ingredient.sort_order), asc(recipe_ingredient.created_at));
}

/** Todas las recetas VIVAS con sus ingredientes (para la generación de lista).
 * Las recetas en papelera NO alimentan la generación de la lista de compra. */
export function getAllRecipesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(recipe)
    .where(and(eq(recipe.user_id, userId), ...liveRecipeFilters()));
}

/** Recetas en la papelera (deleted_at IS NOT NULL), más recientes primero. */
export function getTrashedRecipesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(recipe)
    .where(and(eq(recipe.user_id, userId), isNotNull(recipe.deleted_at)))
    .orderBy(desc(recipe.deleted_at));
}

export function getAllIngredientsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(recipe_ingredient).where(eq(recipe_ingredient.user_id, userId));
}

/**
 * Historial de versiones LOCAL de una receta (L4) — bucket PowerSync acotado a
 * la ventana de sync (últimas 10). id UUIDv7 ⇒ orden descendente = más
 * reciente primero. El historial profundo se pide bajo demanda por API.
 */
export function getRecipeVersionsQuery(db: PowerSyncDrizzleDB, recipeId: string) {
  return db
    .select()
    .from(recipe_version)
    .where(eq(recipe_version.recipe_id, recipeId))
    .orderBy(desc(recipe_version.id));
}

/**
 * Recetas del usuario que contienen un ingrediente cuyo nombre coincide
 * (LIKE, case-insensitive vía COLLATE NOCASE de SQLite por defecto en LIKE).
 * Interconexión: ingrediente/despensa → recetas que lo usan (Fase 5, eje 6).
 */
export function getRecipesByIngredientNameQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  ingredientName: string,
) {
  const pattern = `%${ingredientName.trim()}%`;
  return db
    .selectDistinct({
      id: recipe.id,
      name: recipe.name,
      servings: recipe.servings,
      photo_url: recipe.photo_url,
    })
    .from(recipe_ingredient)
    .innerJoin(recipe, eq(recipe_ingredient.recipe_id, recipe.id))
    .where(
      and(
        eq(recipe.user_id, userId),
        ...liveRecipeFilters(),
        like(recipe_ingredient.name, pattern),
      ),
    )
    .orderBy(desc(recipe.updated_at));
}

export type RecipeByIngredientRow = Awaited<
  ReturnType<ReturnType<typeof getRecipesByIngredientNameQuery>["execute"]>
>[number];
